import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode, type RefObject } from 'react';
import type { ExperienceConfiguration } from '@ayumia/contracts';
import { formatRoute, type Route } from '../app/router';
import { BrandMark } from '../design-system/brand';
import { Icon } from '../design-system/icons';
import type { Density } from './density-preference';
import { initials, labelOf, routeOfItem, type NavItem } from './navigation-model';

/**
 * Barre du haut (ADR-092, référence « ULTIM APP ») : pleine largeur, 60 px, fond `shell.topbar`.
 * À gauche le carré de logo à monogramme, le mot du produit et le tenant ; à droite la bascule de
 * densité, la pilule du tenant (celui de l'identité, seul), la pilule de site (entité courante ;
 * changement en file d'attente), les boutons carrés (nouvelle fenêtre, aide, guides vidéo,
 * assistant IA) et l'avatar qui ouvre le menu utilisateur. Les panneaux d'aide, de guides et
 * d'assistant sont **vides** (« Bientôt disponible ») : aucun appel de modèle, aucune requête.
 */

type ExperienceContext = ExperienceConfiguration['context'];
export type TopbarPanel = 'help' | 'guides' | 'assistant';

/** Menu déroulant de la barre : Échap, clic hors du menu, focus rendu au déclencheur. */
function useDropdown(): {
  open: boolean;
  setOpen: (open: boolean) => void;
  wrap: RefObject<HTMLDivElement | null>;
  trigger: RefObject<HTMLButtonElement | null>;
  close: () => void;
} {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement | null>(null);
  const trigger = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent): void => {
      if (wrap.current !== null && event.target instanceof Node && !wrap.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);
  const close = (): void => {
    setOpen(false);
    trigger.current?.focus();
  };
  return { open, setOpen, wrap, trigger, close };
}

function menuKeys(event: ReactKeyboardEvent<HTMLElement>, container: HTMLElement | null, close: () => void, onTab: () => void): void {
  const entries = [...(container?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"]), [role="menuitemradio"]') ?? [])];
  const index = entries.indexOf(document.activeElement as HTMLElement);
  if (event.key === 'ArrowDown') entries[(index + 1) % entries.length]?.focus();
  else if (event.key === 'ArrowUp') entries[(index - 1 + entries.length) % entries.length]?.focus();
  else if (event.key === 'Home') entries[0]?.focus();
  else if (event.key === 'End') entries[entries.length - 1]?.focus();
  else if (event.key === 'Escape') close();
  else if (event.key === 'Tab') return onTab();
  else return;
  event.preventDefault();
}

/** Pilule de contexte (tenant, site) avec sa liste : présentation seulement. */
function ContextPill({ icon, label, ariaLabel, children, note }: { icon: string; label: string; ariaLabel: string; children: (close: () => void) => ReactNode; note?: string }) {
  const { open, setOpen, wrap, trigger, close } = useDropdown();
  const list = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (open) queueMicrotask(() => list.current?.querySelector<HTMLElement>('[role="menuitemradio"]')?.focus());
  }, [open]);
  return (
    <div className="ay-topbar__dropdown" ref={wrap}>
      <button ref={trigger} type="button" className="ay-pillbutton" aria-haspopup="menu" aria-expanded={open} aria-label={ariaLabel} onClick={() => setOpen(!open)}>
        <Icon name={icon} size={15} />
        <span className="ay-pillbutton__label">{label}</span>
        <Icon name="chevron-down" size={13} />
      </button>
      {open && (
        <div ref={list} className="ay-topbar__menu-list" role="menu" aria-label={ariaLabel} onKeyDown={(event) => menuKeys(event, list.current, close, () => setOpen(false))}>
          {children(close)}
          {note !== undefined && <p className="ay-topbar__menu-note">{note}</p>}
        </div>
      )}
    </div>
  );
}

/** Menu utilisateur, ouvert par l'avatar (`role="menu"`, clavier). */
function UserMenu({ name, extra, onNavigate, onSignOut }: { name: string; extra: readonly NavItem[]; onNavigate: (route: Route) => void; onSignOut: () => void }) {
  const { open, setOpen, wrap, trigger, close } = useDropdown();
  const menu = useRef<HTMLUListElement | null>(null);
  useEffect(() => {
    if (open) queueMicrotask(() => menu.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus());
  }, [open]);
  const go = (route: Route): void => {
    setOpen(false);
    onNavigate(route);
  };
  return (
    <div className="ay-usermenu" ref={wrap}>
      <button
        type="button"
        ref={trigger}
        className="ay-usermenu__button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? 'ay-usermenu-list' : undefined}
        title={name}
        onClick={() => setOpen(!open)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        <span className="ay-avatar ay-avatar--lg" aria-hidden="true">
          {initials(name)}
        </span>
        <span className="ay-visually-hidden ay-usermenu__name">{name}</span>
      </button>
      {open && (
        <ul id="ay-usermenu-list" ref={menu} className="ay-usermenu__list" role="menu" aria-label="Menu utilisateur" onKeyDown={(event) => menuKeys(event, menu.current, close, () => setOpen(false))}>
          <li role="presentation" className="ay-usermenu__who">
            {name}
          </li>
          <li role="none">
            <button type="button" role="menuitem" tabIndex={-1} onClick={() => go({ kind: 'account' })}>
              <Icon name="shield" size={16} /> Sécurité du compte
            </button>
          </li>
          {extra.map((item) => {
            const route = routeOfItem(item);
            if (route === null) return null;
            return (
              <li role="none" key={item.item_id}>
                <button type="button" role="menuitem" tabIndex={-1} onClick={() => go(route)}>
                  <Icon name={item.icon} size={16} /> {labelOf(item)}
                </button>
              </li>
            );
          })}
          <li role="separator" className="ay-usermenu__separator" />
          <li role="none">
            <button
              type="button"
              role="menuitem"
              tabIndex={-1}
              className="ay-usermenu__signout"
              onClick={() => {
                setOpen(false);
                onSignOut();
              }}
            >
              <Icon name="logout" size={16} /> Se déconnecter
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}

/** Bascule de densité « Normal / Condensé » (`role="radiogroup"`). */
function DensitySwitch({ density, onChange }: { density: Density; onChange: (density: Density) => void }) {
  const options: Array<{ value: Density; label: string }> = [
    { value: 'normal', label: 'Normal' },
    { value: 'compact', label: 'Condensé' },
  ];
  const move = (event: ReactKeyboardEvent<HTMLButtonElement>): void => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const next = density === 'normal' ? 'compact' : 'normal';
    onChange(next);
    queueMicrotask(() => (event.currentTarget.parentElement?.querySelector<HTMLElement>(`[data-density="${next}"]`) ?? null)?.focus());
  };
  return (
    <div className="ay-segmented" role="radiogroup" aria-label="Densité d’affichage">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          data-density={option.value}
          aria-checked={density === option.value}
          tabIndex={density === option.value ? 0 : -1}
          className="ay-segmented__option"
          onClick={() => onChange(option.value)}
          onKeyDown={move}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** Panneau latéral de la barre (aide, guides, assistant) : vide pour l'instant, aucune requête. */
export function TopbarSidePanel({ panel, onClose }: { panel: TopbarPanel; onClose: () => void }) {
  const close = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    close.current?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  const titles: Record<TopbarPanel, string> = { help: 'Aide', guides: 'Guides vidéo', assistant: 'Assistant IA' };
  return (
    <aside className="ay-sidepanel" role="complementary" aria-labelledby="ay-sidepanel-title">
      <header className="ay-sidepanel__header">
        <h2 id="ay-sidepanel-title">
          {panel === 'assistant' && <Icon name="sparkle" size={16} />} {titles[panel]}
        </h2>
        <button ref={close} type="button" className="ay-iconbox ay-iconbox--light" aria-label="Fermer le panneau" onClick={onClose}>
          <Icon name="close" size={16} />
        </button>
      </header>
      <div className="ay-sidepanel__body">
        <p className="ay-sidepanel__soon">Bientôt disponible.</p>
        {panel === 'assistant' && <p className="ay-text--muted">L’assistant passera par les décisions gouvernées d’AYUMIA ; rien n’est envoyé à un modèle depuis ce panneau.</p>}
      </div>
    </aside>
  );
}

export interface TopbarProps {
  context: ExperienceContext | undefined;
  tenantLabel: string;
  productName: string;
  name: string;
  userMenuItems: readonly NavItem[];
  density: Density;
  onDensity: (density: Density) => void;
  panel: TopbarPanel | null;
  onPanel: (panel: TopbarPanel | null) => void;
  menuOpen: boolean;
  onMenu: () => void;
  onNavigate: (route: Route) => void;
  onSignOut: () => void;
}

export function Topbar({ context, tenantLabel, productName, name, userMenuItems, density, onDensity, panel, onPanel, menuOpen, onMenu, onNavigate, onSignOut }: TopbarProps) {
  const entities = context?.entities ?? [];
  const entityLabel = context?.entity_label ?? entities.find((entity) => entity.entity_id === context?.entity_id)?.label;
  const toggle = (next: TopbarPanel): void => onPanel(panel === next ? null : next);
  const box = (key: TopbarPanel | 'window', icon: string, label: string, onClick: () => void, extra = '') => (
    <button key={key} type="button" className={`ay-iconbox${extra}`} aria-label={label} title={label} aria-pressed={key === 'window' ? undefined : panel === key} onClick={onClick}>
      <Icon name={icon} size={16} />
    </button>
  );
  return (
    <header className="ay-topbar">
      <button type="button" className="ay-iconbox ay-topbar__menu" aria-label="Menu" aria-expanded={menuOpen} aria-controls="ay-sidemenu" onClick={onMenu}>
        <Icon name={menuOpen ? 'close' : 'menu'} />
      </button>
      <a
        className="ay-topbar__home"
        href={formatRoute({ kind: 'home' })}
        aria-label={`Accueil — ${tenantLabel}`}
        onClick={(event) => {
          event.preventDefault();
          onNavigate({ kind: 'home' });
        }}
      >
        <BrandMark label={tenantLabel} />
        <span className="ay-topbar__brand">{productName}</span>
      </a>
      <span className="ay-topbar__divider" aria-hidden="true" />
      <span className="ay-topbar__tenant">{tenantLabel}</span>
      <span className="ay-topbar__spacer" />
      <DensitySwitch density={density} onChange={onDensity} />
      <ContextPill icon="building" label={tenantLabel} ariaLabel={`Tenant : ${tenantLabel}`}>
        {(close) => (
          <button type="button" role="menuitemradio" aria-checked="true" tabIndex={-1} onClick={close}>
            <Icon name="check" size={14} /> {tenantLabel}
          </button>
        )}
      </ContextPill>
      {entityLabel !== undefined && (
        <ContextPill icon="building" label={entityLabel} ariaLabel={`Site : ${entityLabel}`} {...(entities.length > 1 ? { note: 'Changement de site : bientôt disponible.' } : {})}>
          {(close) =>
            (entities.length > 0 ? entities : [{ entity_id: context?.entity_id ?? 'current', label: entityLabel }]).map((entity) => {
              const current = entity.entity_id === context?.entity_id || (context?.entity_id === undefined && entity.label === entityLabel);
              return (
                <button key={entity.entity_id} type="button" role="menuitemradio" aria-checked={current} aria-disabled={current ? undefined : true} tabIndex={-1} onClick={current ? close : undefined}>
                  {current ? <Icon name="check" size={14} /> : <span className="ay-topbar__menu-spacer" />} {entity.label}
                </button>
              );
            })
          }
        </ContextPill>
      )}
      <span className="ay-topbar__tools">
        {box('window', 'external-link', 'Ouvrir dans une nouvelle fenêtre', () => window.open(window.location.href, '_blank', 'noopener'))}
        {box('help', 'help', 'Aide', () => toggle('help'))}
        {box('guides', 'video', 'Guides vidéo', () => toggle('guides'))}
        {box('assistant', 'sparkle', 'Assistant IA', () => toggle('assistant'), ' ay-iconbox--accent')}
      </span>
      <UserMenu name={name} extra={userMenuItems} onNavigate={onNavigate} onSignOut={onSignOut} />
    </header>
  );
}
